import { Device } from '@twilio/voice-sdk';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';
import { DefaultUserStatuses } from '../constants/UserStatusesEnum';
import { CallDeviceStatusEnum, CallActivityDirectionEnum } from '../constants/CallStatusEnum';
import { ContactTypeEnum } from '../constants/ContactTypeEnum';
import { CallBarState } from '../constants/CallBarState';
import { CallMedium } from '../Enums/ActivityEnum';
import { SnackbarType } from '../constants/SnackbarType';

import ContactService from '../Services/ContactService';
import TwilioService from "../Services/Twilio/TwilioService";
import CallNotificationService from '../Services/Call/CallNotification';
import StreamProcessManagerService from '../Services/StreamProcessManagerService';
import SaleforceService from './SaleforceService';
import CallActivityService from './Call/CallActivity';

import { showSnackbar } from '../slices/snackbar';
import { getTwilioData } from '../slices/twilio';
import { callStatusChange, setVoiceRegistration } from '../slices/callState';
import { clearCallingContact } from '../slices/callingContact';
import { updateUserStatus, addLastCallRecord } from '../slices/auth';
import { clearResumingCallDetails } from '../slices/callState';
import { cleanupContactSearchByPhone } from '../slices/contactSearchByPhone';
import {
  updateCallBarState, setIncomingCallIdle,
  setIncomingCallTriggered, updateCurrentActiveCallRecordId,
  setCallPrimaryMetadata, getCallIncoming,
} from '../slices/callCentre';
import { store } from '../app/store';
import { logger } from '../Util/Logger/ClientLogger';
import { NetworkAvailability } from '../Util/NetworkAvailability';
import { resolveTwilioVoiceLogLevel } from '../Util/twilioVoiceLogLevel';
import { resolveTwilioVoiceEdge, twilioVoiceEdgeKey } from '../Util/twilioVoiceEdge';
import {
  isVoiceTabLeader,
  noteVoiceLeaderCallActive,
  onVoiceLeadershipChange,
  requestVoiceLeadership,
  setVoiceLeaderActiveCallProbe,
  setVoiceLeaderYieldHandler,
  startVoiceTabLeader,
} from '../Util/voiceTabLeader';
import {
  finishRemoteIncoming,
  presentIncomingCall,
  startIncomingUiRelay,
} from '../Util/incomingUiRelay';
import { appDebugLog, appDebugWarn } from '../Util/appDebugLog';
import { isJwtExpired } from '../Util/jwtUtils';
import { refreshAccessToken } from './instances/VoiceIQAPI';

const LogTitle = 'TwilioVoiceService';
const CALLCONNECTIONMC = "rocketphone__CALLCONNECTIONMC__c";

/** Twilio Voice `customParameters` are often a `Map`; flatten for logger `metaJson`. */
const metaFromTwilioCustomParameters = (customParameters: any): Record<string, string> => {
  if (customParameters == null) return {};
  if (typeof customParameters.forEach === 'function') {
    const out: Record<string, string> = {};
    customParameters.forEach((value: string, key: string) => {
      out[String(key)] = String(value);
    });
    return out;
  }
  return typeof customParameters === 'object' ? { ...customParameters } : {};
};

export default class TwilioVoiceService {
  static callConnection: any = null;
  static streamProcessManager = new StreamProcessManagerService(); // The real time transcription socket map manager.
  static twilioDevice: any = null;
  static pendingConnectParams: any = null;
  /** Last region used for a full Device setup; used to avoid silent wrong-edge when region changes. */
  static lastTwilioSetupRegionId: string | null = null;
  /** Set immediately before a local disconnect(); read in disconnectHandler to distinguish user vs SDK drop. */
  static pendingDisconnectInitiator: { initiator: 'user' | 'app'; source: string } | null = null;
  /** Token refresh deferred while a call leg is active — updateToken during a call triggers 31209 and drops the leg. */
  static pendingTokenRefreshReason: string | null = null;
  static pendingTwilioToken: string | null = null;
  /** Avoid stacking multiple token fetches when sleep/offline events fire repeatedly. */
  static pendingOnlineTokenRefresh = false;
  static pendingOnlineReRegistration = false;
  /** This tab is giving the Twilio Device to another tab — ignore unregister/error churn. */
  static handingOff = false;
  static voiceLeaderReady = false;
  static pendingVoiceSetup: {
    twilioToken: string;
    effectiveRegionId: string;
    callStateToRestore: string;
  } | null = null;
  static voiceSetupGate: Promise<void> | null = null;
  static reRegisterTimer: ReturnType<typeof setTimeout> | null = null;
  static reRegisterAttempt = 0;
  /** 20101/31205/20104: do not register again until a new Twilio JWT is fetched. */
  static tokenRefreshRequired = false;
  static expiredTokenRefresh: Promise<boolean> | null = null;
  static registerInFlight = false;
  /** Ignore a 20101 from the register that was already in flight when the new token landed. */
  static ignoreInvalidTokenUntil = 0;
  static lastAvailabilitySyncAt = 0;

  static availabilityPushInFlight = false;

  static availabilityPushQueued = false;
  static lastRegistrationNoticeAt = 0;

  static markUserInitiatedDisconnect = (source: string) => {
    TwilioVoiceService.pendingDisconnectInitiator = { initiator: 'user', source };
  };

  static markAppInitiatedDisconnect = (source: string) => {
    TwilioVoiceService.pendingDisconnectInitiator = { initiator: 'app', source };
  };

  static resolveDisconnectInitiator = (): { initiator: 'user' | 'app' | 'sdk'; source: string } => {
    const pending = TwilioVoiceService.pendingDisconnectInitiator;
    TwilioVoiceService.pendingDisconnectInitiator = null;
    return pending ?? { initiator: 'sdk', source: 'twilio-sdk' };
  };

  /** Inbound invite still pending/ringing — avoid token/signaling refresh that races `accept()`. */
  static shouldSkipTokenRefreshForEarlyInboundLeg = (): boolean => {
    const conn = TwilioVoiceService.callConnection;
    if (!conn || typeof conn.status !== 'function') {
      return false;
    }
    const s = conn.status();
    return s === 'pending' || s === 'ringing';
  };

  /** Answered or connecting leg — device re-register / destroy must not run during this window. */
  static hasActiveCallLeg = (): boolean => {
    const conn = TwilioVoiceService.callConnection;
    if (!conn || typeof conn.status !== 'function') {
      return false;
    }
    const status = conn.status();
    return status === 'open' || status === 'connecting';
  };

  static getTwilioErrorCode = (error: any): number | undefined =>
    error?.code ?? error?.originalError?.code ?? error?.twilioError?.code;

  static isRecoverableTokenError = (error: any): boolean => {
    const code = TwilioVoiceService.getTwilioErrorCode(error);
    return code === 20101 || code === 20104 || code === 31205 || code === 31209;
  };

  /** 31205 is the signaling code Twilio sends when the access token JWT is already expired. */
  static isExpiredAccessTokenError = (error: any): boolean => {
    const codes = [
      error?.code,
      error?.originalError?.code,
      error?.twilioError?.code,
      error?.originalError?.twilioError?.code,
    ];
    return codes.some((code) => code === 20101 || code === 20104 || code === 31205);
  };

  /** Websocket/registration races. Refreshing the token here makes the next register fail with 20101. */
  static isSignalingTransportError = (error: any): boolean => {
    const codes = [
      error?.code,
      error?.originalError?.code,
      error?.twilioError?.code,
      error?.originalError?.twilioError?.code,
    ];
    return codes.some((code) =>
      code === 31000 || code === 31005 || code === 31009 || code === 31204 || code === 53000 || code === 53001
    );
  };

  static ensureVoiceLeader() {
    if (TwilioVoiceService.voiceLeaderReady) return;
    TwilioVoiceService.voiceLeaderReady = true;
    setVoiceLeaderActiveCallProbe(() => !!TwilioVoiceService.callConnection);
    setVoiceLeaderYieldHandler(() => TwilioVoiceService.releaseVoiceLineForHandoff());
    onVoiceLeadershipChange((isLeader) => {
      const userId = store.getState()?.auth?.authUser?.userId;
      logger.info(`${LogTitle} voice leadership ${isLeader ? 'acquired' : 'released'}`, LogTitle, { userId });
      if (!isLeader) {
        TwilioVoiceService.handingOff = false;
        if (TwilioVoiceService.twilioDevice && !TwilioVoiceService.callConnection) {
          TwilioVoiceService.releaseVoiceLineForHandoff();
        }
        return;
      }
      const pending = TwilioVoiceService.pendingVoiceSetup;
      if (!pending) return;
      void TwilioVoiceService.setupTwilio(
        pending.twilioToken,
        pending.effectiveRegionId,
        pending.callStateToRestore,
      );
    });
    startVoiceTabLeader();
    startIncomingUiRelay({
      show: (payload) => {
        store.dispatch(setIncomingCallTriggered(payload));
      },
      clearLocal: () => {
        store.dispatch(setIncomingCallIdle());
      },
      answer: () => {
        if (!isVoiceTabLeader()) return;
        const connection = TwilioVoiceService.callConnection;
        const status = typeof connection?.status === 'function' ? connection.status() : '';
        if (connection && (status === 'pending' || status === 'ringing')) {
          connection.accept();
        }
      },
      decline: () => {
        if (!isVoiceTabLeader()) return;
        const connection = TwilioVoiceService.callConnection;
        const status = typeof connection?.status === 'function' ? connection.status() : '';
        if (!connection) return;
        try {
          if (status === 'pending' || status === 'ringing') {
            TwilioVoiceService.markUserInitiatedDisconnect('remoteIncoming.decline');
            connection.reject();
          } else if (status === 'connecting' || status === 'open') {
            TwilioVoiceService.markUserInitiatedDisconnect('remoteIncoming.decline');
            connection.disconnect();
          }
        } catch (error) {
          logger.warn(`${LogTitle} remote decline failed`, LogTitle, { error, status });
        }
      },
    });
  }

  /** Unregister so another tab can be the only Device on this identity. False while a call leg exists. */
  static releaseVoiceLineForHandoff(): boolean {
    if (TwilioVoiceService.callConnection) return false;
    TwilioVoiceService.handingOff = true;
    if (TwilioVoiceService.reRegisterTimer) {
      clearTimeout(TwilioVoiceService.reRegisterTimer);
      TwilioVoiceService.reRegisterTimer = null;
    }
    const device = TwilioVoiceService.twilioDevice;
    TwilioVoiceService.twilioDevice = null;
    try {
      if (device && device.state !== 'destroyed') {
        device.destroy();
      }
    } catch (error) {
      logger.warn(`${LogTitle} releaseVoiceLineForHandoff failed`, LogTitle, { error });
    } finally {
      TwilioVoiceService.handingOff = false;
    }
    return true;
  }

  static shouldDeferTokenRefresh = (): boolean =>
    TwilioVoiceService.hasActiveCallLeg() ||
    TwilioVoiceService.shouldSkipTokenRefreshForEarlyInboundLeg();

  static scheduleDeferredTokenRefresh = (reason: string, twilioToken?: string) => {
    TwilioVoiceService.pendingTokenRefreshReason = reason;
    if (twilioToken) {
      TwilioVoiceService.pendingTwilioToken = twilioToken;
    }
    const userId = store.getState()?.auth?.authUser?.userId;
    logger.info(`${LogTitle} Deferred token refresh until call ends`, LogTitle, {
      userId,
      reason,
      hasActiveCallLeg: TwilioVoiceService.hasActiveCallLeg(),
      deviceState: TwilioVoiceService.twilioDevice?.state,
    });
  };

  static applyPendingTokenRefresh = async () => {
    const reason = TwilioVoiceService.pendingTokenRefreshReason;
    if (!reason || !TwilioVoiceService.twilioDevice) {
      return;
    }

    TwilioVoiceService.pendingTokenRefreshReason = null;
    const cachedToken = TwilioVoiceService.pendingTwilioToken;
    TwilioVoiceService.pendingTwilioToken = null;

    if (cachedToken) {
      try {
        TwilioVoiceService.twilioDevice.updateToken(cachedToken);
        logger.info(`${LogTitle} applyPendingTokenRefresh: updateToken (cached)`, LogTitle, {
          userId: store.getState()?.auth?.authUser?.userId,
          reason,
        });
      } catch (tokenErr) {
        logger.error(`${LogTitle} applyPendingTokenRefresh: cached updateToken failed`, LogTitle, { reason, tokenErr });
        await TwilioVoiceService.fetchAndUpdateTwilioToken(`deferred:${reason}`, { force: true });
      }
      return;
    }

    await TwilioVoiceService.fetchAndUpdateTwilioToken(`deferred:${reason}`, { force: true });
  };

  static describeTwilioError(error: any) {
    return {
      errorCode: TwilioVoiceService.getTwilioErrorCode(error),
      message: error?.message || error?.originalError?.message || (error ? String(error) : ''),
      name: error?.name,
    };
  }

  /**
   * Gateway Bearer must be valid before POST /api/v1/twilio/token.
   * An expired sign-in token makes the Twilio JWT fetch fail, and the device then keeps registering with the old voice token.
   */
  static async ensureGatewayAccessToken(reason: string): Promise<string | null> {
    const userId = store.getState()?.auth?.authUser?.userId;
    let accessToken = store.getState()?.auth?.authUser?.AccessToken as string | undefined;
    if (accessToken && !isJwtExpired(accessToken, 60_000)) {
      return accessToken;
    }

    logger.warn(`${LogTitle} gateway access token expired — refreshing before Twilio token fetch`, LogTitle, {
      userId,
      reason,
    });
    try {
      accessToken = await refreshAccessToken();
    } catch (refreshErr) {
      logger.error(`${LogTitle} gateway refresh failed before Twilio token fetch`, LogTitle, {
        userId,
        reason,
        message: (refreshErr as Error)?.message || String(refreshErr),
      });
      TwilioVoiceService.noteRegistrationTrouble(
        'failed',
        'Phone registration failed. Your session expired. Refresh the page and sign in again.',
      );
      return null;
    }

    accessToken = store.getState()?.auth?.authUser?.AccessToken || accessToken;
    if (!accessToken || isJwtExpired(accessToken, 0)) {
      TwilioVoiceService.noteRegistrationTrouble(
        'failed',
        'Phone registration failed. Your session expired. Refresh the page and sign in again.',
      );
      return null;
    }
    return accessToken;
  }

  /**
   * Rewrite the status the UI is showing into beacon Redis.
   * A green Available with no Redis row is not dialable.
   * beacon-connected and device-registered are not throttled: those are the moments
   * a dropped socket has to put the user back on the route.
   */
  static syncShownAvailabilityToRedis(reason: string, force = false) {
    const recovery = force || reason === 'beacon-connected' || reason === 'device-registered';
    const now = Date.now();
    if (!recovery && now - TwilioVoiceService.lastAvailabilitySyncAt < 8_000) {
      return;
    }
    if (!force && store.getState()?.callCentre?.currentActiveCallRecordId) {
      return;
    }
    const userStatusId = store.getState()?.auth?.user?.userStatus?.id;
    const statuses = store.getState()?.settings?.tenantSettings?.userStatuses;
    const matched = statuses?.find((item: { id?: string; isAvailable?: boolean; isAwayFromDesk?: boolean }) => item?.id === userStatusId);
    if (!matched?.id) {
      return;
    }
    if (TwilioVoiceService.availabilityPushInFlight) {
      if (recovery) {
        TwilioVoiceService.availabilityPushQueued = true;
      }
      return;
    }
    void TwilioVoiceService.pushShownAvailability(reason, {
      id: matched.id,
      isAvailable: !!matched.isAvailable,
      isAwayFromDesk: !!matched.isAwayFromDesk,
    });
  }

  static async pushShownAvailability(
    reason: string,
    matched: { id: string; isAvailable: boolean; isAwayFromDesk: boolean },
  ) {
    TwilioVoiceService.availabilityPushInFlight = true;
    logger.info(`${LogTitle} syncing shown availability to beacon`, LogTitle, {
      reason,
      userStatusId: matched.id,
      available: matched.isAvailable,
    });
    let ok = false;
    try {
      for (let attempt = 1; attempt <= 2 && !ok; attempt += 1) {
        const action: any = await store.dispatch(updateUserStatus({
          UserStatusId: matched.id,
          Available: matched.isAvailable,
          AwayFromDesk: matched.isAwayFromDesk,
          // Not on a call. Beacon must clear a stale ActiveCallId or the route skips this user.
          ConnectedCallId: null,
        }));
        ok = action?.meta?.requestStatus === 'fulfilled';
        if (!ok && attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      }
      if (ok) {
        TwilioVoiceService.lastAvailabilitySyncAt = Date.now();
        logger.info(`${LogTitle} availability sync succeeded`, LogTitle, {
          reason,
          userStatusId: matched.id,
          available: matched.isAvailable,
        });
      } else {
        logger.error(`${LogTitle} availability sync failed`, LogTitle, {
          reason,
          userStatusId: matched.id,
          available: matched.isAvailable,
        });
      }
    } finally {
      TwilioVoiceService.availabilityPushInFlight = false;
      if (TwilioVoiceService.availabilityPushQueued) {
        TwilioVoiceService.availabilityPushQueued = false;
        TwilioVoiceService.syncShownAvailabilityToRedis('beacon-connected');
      }
    }
  }

  static noteRegistrationReady() {
    if (TwilioVoiceService.handingOff) {
      return;
    }
    const previous = store.getState()?.callState?.voiceRegistration;
    store.dispatch(setVoiceRegistration({ state: 'registered', message: '' }));
    if (previous === 'trouble' || previous === 'failed') {
      store.dispatch(showSnackbar({
        snackbarType: 'Info',
        snackbarData: { message: 'Phone registered. You can take calls.' },
      }));
    }
    TwilioVoiceService.syncShownAvailabilityToRedis('device-registered');
  }

  static noteRegistrationTrouble(level: 'trouble' | 'failed', message: string) {
    if (TwilioVoiceService.handingOff || TwilioVoiceService.hasActiveCallLeg()) {
      return;
    }
    const current = store.getState()?.callState?.voiceRegistration;
    if (current === 'failed' && level === 'trouble') {
      return;
    }
    store.dispatch(setVoiceRegistration({ state: level, message }));
    const now = Date.now();
    if (level === 'trouble' && now - TwilioVoiceService.lastRegistrationNoticeAt < 25_000) {
      return;
    }
    TwilioVoiceService.lastRegistrationNoticeAt = now;
    store.dispatch(showSnackbar({
      snackbarType: SnackbarType.Error,
      snackbarData: { message },
    }));
  }

  static async mintTwilioToken(reason: string): Promise<string | null> {
    const userId = store.getState()?.auth?.authUser?.userId;
    const tenantCode = store.getState()?.auth?.authUser?.tenantCode;
    const AccessToken = await TwilioVoiceService.ensureGatewayAccessToken(reason);
    if (!tenantCode || !AccessToken || !userId) {
      logger.error(`${LogTitle} mintTwilioToken: missing auth`, LogTitle, { userId, reason });
      return null;
    }
    let result;
    try {
      result = await TwilioService.getTwilioData(tenantCode, AccessToken, { callerId: userId });
    } catch (tokenErr) {
      if (NetworkAvailability.isNetworkError(tokenErr)) {
        logger.warn(`${LogTitle} mintTwilioToken network error`, LogTitle, { userId, reason });
        return null;
      }
      throw tokenErr;
    }
    const twilioToken = result.data?.data?.token as string | undefined;
    if (!twilioToken) {
      return null;
    }
    if (TwilioVoiceService.pendingVoiceSetup) {
      TwilioVoiceService.pendingVoiceSetup = {
        ...TwilioVoiceService.pendingVoiceSetup,
        twilioToken,
      };
    }
    return twilioToken;
  }

  /** Fetch a new Twilio JWT and stop the register loop that keeps sending the expired one. */
  static refreshExpiredTwilioToken(): Promise<boolean> {
    TwilioVoiceService.tokenRefreshRequired = true;
    if (TwilioVoiceService.reRegisterTimer) {
      clearTimeout(TwilioVoiceService.reRegisterTimer);
      TwilioVoiceService.reRegisterTimer = null;
    }
    if (TwilioVoiceService.expiredTokenRefresh) {
      return TwilioVoiceService.expiredTokenRefresh;
    }
    TwilioVoiceService.expiredTokenRefresh = TwilioVoiceService.fetchAndUpdateTwilioToken('jwt-expired', { force: true })
      .then((ok) => {
        if (ok) {
          TwilioVoiceService.tokenRefreshRequired = false;
          TwilioVoiceService.reRegisterAttempt = 0;
        } else {
          setTimeout(() => {
            if (TwilioVoiceService.tokenRefreshRequired) {
              TwilioVoiceService.scheduleReRegistration('jwt-expired-retry');
            }
          }, 0);
        }
        return ok;
      })
      .finally(() => {
        TwilioVoiceService.expiredTokenRefresh = null;
      });
    return TwilioVoiceService.expiredTokenRefresh;
  }

  static fetchAndUpdateTwilioToken = async (
    reason: string,
    options?: { force?: boolean },
  ): Promise<boolean> => {
    const userId = store.getState()?.auth?.authUser?.userId;
    if (!TwilioVoiceService.twilioDevice || !isVoiceTabLeader() || TwilioVoiceService.handingOff) {
      return false;
    }
    const deviceState = TwilioVoiceService.twilioDevice.state;
    if (deviceState === 'destroyed' || (deviceState === 'registering' && !options?.force)) {
      logger.info(`${LogTitle} fetchAndUpdateTwilioToken skipped`, LogTitle, { userId, reason, deviceState });
      return false;
    }

    if (!NetworkAvailability.isOnline()) {
      appDebugWarn(`${LogTitle} fetchAndUpdateTwilioToken deferred — offline`, { userId, reason });
      if (!TwilioVoiceService.pendingOnlineTokenRefresh) {
        TwilioVoiceService.pendingOnlineTokenRefresh = true;
        NetworkAvailability.onceOnline(() => {
          TwilioVoiceService.pendingOnlineTokenRefresh = false;
          void TwilioVoiceService.fetchAndUpdateTwilioToken(`online:${reason}`, { force: true });
        });
      }
      return false;
    }

    if (!options?.force && TwilioVoiceService.shouldDeferTokenRefresh()) {
      TwilioVoiceService.scheduleDeferredTokenRefresh(reason);
      return true;
    }

    try {
      const twilioToken = await TwilioVoiceService.mintTwilioToken(reason);
      if (!twilioToken) {
        logger.error(`${LogTitle} fetchAndUpdateTwilioToken: no token returned`, LogTitle, { userId, reason });
        TwilioVoiceService.noteRegistrationTrouble(
          'trouble',
          'Having trouble registering your phone. Refreshing your session…',
        );
        return false;
      }

      TwilioVoiceService.twilioDevice.updateToken(twilioToken);
      TwilioVoiceService.tokenRefreshRequired = false;
      TwilioVoiceService.ignoreInvalidTokenUntil = Date.now() + 4_000;
      logger.info(`${LogTitle} fetchAndUpdateTwilioToken: updateToken`, LogTitle, {
        userId,
        reason,
        hasActiveCallLeg: TwilioVoiceService.hasActiveCallLeg(),
        deviceState: TwilioVoiceService.twilioDevice.state,
      });
      if (TwilioVoiceService.twilioDevice.state === 'unregistered') {
        TwilioVoiceService.scheduleReRegistration('after-token-refresh');
      }
      return true;
    } catch (tokenErr) {
      if (NetworkAvailability.isNetworkError(tokenErr)) {
        appDebugWarn(`${LogTitle} fetchAndUpdateTwilioToken network error — will retry when online`, {
          userId,
          reason,
        });
        if (!TwilioVoiceService.pendingOnlineTokenRefresh) {
          TwilioVoiceService.pendingOnlineTokenRefresh = true;
          NetworkAvailability.onceOnline(() => {
            TwilioVoiceService.pendingOnlineTokenRefresh = false;
            void TwilioVoiceService.fetchAndUpdateTwilioToken(`online-retry:${reason}`, { force: true });
          });
        }
        return false;
      }
      logger.error(`${LogTitle} fetchAndUpdateTwilioToken failed`, LogTitle, {
        userId,
        reason,
        ...TwilioVoiceService.describeTwilioError(tokenErr),
      });
      TwilioVoiceService.noteRegistrationTrouble(
        'trouble',
        'Having trouble registering your phone. Refreshing your session…',
      );
      return false;
    }
  };
  static CallDirectionEnum = Object.freeze({
    incoming: 'INCOMING',
    outgoing: 'OUTGOING',
  });

  /**
   * This sets up the twilio device for this instance of the app.
   * @static
   * @param {string} twilioToken        The twilio token that is sent from the backend
   * @param {string} effectiveRegionId  Specifies which Twilio Data Center to use when registering, initiating calls, and receiving calls.
   * @param {string} callStateToRestore Override the call state with this value.
   *                                    Useful to restore in-call state if refresh happened in the middle of a call.
   *                                    Default: CallDeviceStatusEnum.Ready
   */

  static async setupTwilio(twilioToken: string, effectiveRegionId: string, callStateToRestore: string = CallDeviceStatusEnum.Ready) {
    // if there is no token, we cannot setup the device.
    if (!twilioToken) {
      // TODO: Need to Log
      return
    };
    TwilioVoiceService.pendingVoiceSetup = { twilioToken, effectiveRegionId, callStateToRestore };
    TwilioVoiceService.ensureVoiceLeader();
    if (!isVoiceTabLeader()) {
      logger.info(`${LogTitle} setupTwilio deferred until this tab is the voice leader`, LogTitle, {
        userId: store.getState()?.auth?.authUser?.userId,
      });
      return;
    }
    if (TwilioVoiceService.voiceSetupGate) {
      return TwilioVoiceService.voiceSetupGate;
    }
    let releaseGate = () => {};
    TwilioVoiceService.voiceSetupGate = new Promise<void>((resolve) => {
      releaseGate = resolve;
    });
    const userId = store.getState()?.auth?.authUser?.userId;

    const voiceEdge = resolveTwilioVoiceEdge(effectiveRegionId);
    const voiceEdgeKey = twilioVoiceEdgeKey(voiceEdge);

    logger.info(`${LogTitle} setupTwilio for userId: ${userId}`, LogTitle, {
      effectiveRegionId,
      voiceEdge,
      callStateToRestore,
    });

    try {
      if (isJwtExpired(twilioToken, 0)) {
        logger.warn(`${LogTitle} setupTwilio: cached Twilio JWT is expired — fetching a new one`, LogTitle, {
          userId,
        });
        const fresh = await TwilioVoiceService.mintTwilioToken('setup-expired');
        if (fresh) {
          twilioToken = fresh;
        }
      }

      // Active leg: never destroy/recreate or updateToken — both drop signaling (31209).
      if (TwilioVoiceService.callConnection && TwilioVoiceService.twilioDevice) {
        TwilioVoiceService.scheduleDeferredTokenRefresh('setupTwilio', twilioToken);
        return;
      }

      let existing = TwilioVoiceService.twilioDevice;
      if (existing?.state === 'destroyed') {
        TwilioVoiceService.twilioDevice = null;
        existing = null;
      }

      if (existing && existing.state !== 'destroyed') {
        const regionChanged =
          TwilioVoiceService.lastTwilioSetupRegionId != null &&
          voiceEdgeKey !== TwilioVoiceService.lastTwilioSetupRegionId;

        if (regionChanged) {
          logger.warn(`${LogTitle} setupTwilio: region changed — recreating Device`, LogTitle, {
            userId,
            from: TwilioVoiceService.lastTwilioSetupRegionId,
            to: voiceEdgeKey,
            effectiveRegionId,
          });
          try {
            existing.destroy();
          } catch (destroyErr) {
            logger.warn(`${LogTitle} setupTwilio: destroy on region change`, LogTitle, { userId, destroyErr });
          }
          TwilioVoiceService.twilioDevice = null;
        } else {
          try {
            existing.updateToken(twilioToken);
            if (existing.state === 'unregistered') {
              await existing.register();
            } else if (existing.state === 'registered') {
              TwilioVoiceService.noteRegistrationReady();
            }
            TwilioVoiceService.lastTwilioSetupRegionId = voiceEdgeKey;
            logger.info(`${LogTitle} setupTwilio: reused Device (updateToken)`, LogTitle, {
              userId,
              state: existing.state,
              voiceEdge,
            });
            return;
          } catch (reuseErr) {
            logger.warn(`${LogTitle} setupTwilio: reuse failed — recreating Device`, LogTitle, { userId, reuseErr });
            try {
              existing.destroy();
            } catch {
              /* ignore */
            }
            TwilioVoiceService.twilioDevice = null;
          }
        }
      }

      TwilioVoiceService.twilioDevice = new Device(twilioToken, {
        logLevel: resolveTwilioVoiceLogLevel(),
        edge: voiceEdge,
        // Incoming invites while on a call still need VoiceGrant.IncomingAllow on the JWT.
        allowIncomingWhileBusy: true,
        // @ts-ignore
        codecPreferences: ["opus", "pcmu"],
        tokenRefreshMs: 30000,
        maxCallSignalingTimeoutMs: 30000,
        appName : 'RP4S',
        appVersion: '1.0.1',
        closeProtection: true,
      });

      TwilioVoiceService.twilioDevice.on("registered", () => {
        TwilioVoiceService.reRegisterAttempt = 0;
        if (TwilioVoiceService.reRegisterTimer) {
          clearTimeout(TwilioVoiceService.reRegisterTimer);
          TwilioVoiceService.reRegisterTimer = null;
        }
        logger.info(`${LogTitle} The device is registered for userId: ${userId} and to receive incoming calls`, TwilioVoiceService.twilioDevice.edge);
        TwilioVoiceService.noteRegistrationReady();
        this.readyHandler(CallDeviceStatusEnum.Ready);

        if (TwilioVoiceService.pendingConnectParams){ // Have pending connect params, call connect with that
          appDebugLog(`${LogTitle} Retrying pending outbound call...`);
          const user = store.getState()?.auth?.authUser;
          TwilioVoiceService.connect(user, TwilioVoiceService.pendingConnectParams); // 
        }
      });

      TwilioVoiceService.twilioDevice.on("error", TwilioVoiceService.errorHandler);
      TwilioVoiceService.twilioDevice.on("incoming", TwilioVoiceService.incomingHandler);
      TwilioVoiceService.twilioDevice.audio.on('deviceChange', TwilioVoiceService.deviceChangeHandler);
      TwilioVoiceService.twilioDevice.on('tokenWillExpire', TwilioVoiceService.tokenWillExpireHandler);

      ['registering', 'destroyed'].forEach((ev) => {
        TwilioVoiceService.twilioDevice.on(ev, () => {
          appDebugLog(`${LogTitle} Device SDK event: ${ev}`, {
            userId,
            deviceState: TwilioVoiceService.twilioDevice?.state,
          });
        });
      });

      TwilioVoiceService.twilioDevice.on('unregistered', () => {
        appDebugLog(`${LogTitle} Device SDK event: unregistered`, {
          userId,
          deviceState: TwilioVoiceService.twilioDevice?.state,
        });
        TwilioVoiceService.scheduleReRegistration('unregistered');
      });

      // Device must be registered before invites are reliable; await so setup completes registration.
      await TwilioVoiceService.twilioDevice.register();
      TwilioVoiceService.lastTwilioSetupRegionId = voiceEdgeKey;

      CallNotificationService.registerPageVisibilityListener();
      //setInterval(TwilioVoiceService.checkNetworkStatus, 5000); // Check every 5 seconds

    } catch (exception) {
      // store.dispatch(setupTwilioError(exception));
      logger.error(`${LogTitle} Error for userId: ${userId} setting up Twilio Device:`, LogTitle, {
        ...TwilioVoiceService.describeTwilioError(exception),
      });
      TwilioVoiceService.noteRegistrationTrouble(
        'failed',
        'Phone registration failed. Refresh the page to try again.',
      );
    } finally {
      releaseGate();
      TwilioVoiceService.voiceSetupGate = null;
    }
  }

    static scheduleReRegistration = (reason: string) => {
      const userId = store.getState()?.auth?.authUser?.userId;
      if (TwilioVoiceService.handingOff || !isVoiceTabLeader()) return;
      if (TwilioVoiceService.tokenRefreshRequired) {
        if (TwilioVoiceService.expiredTokenRefresh || TwilioVoiceService.reRegisterTimer) return;
        const delay = Math.min(30_000, 2_000 * (2 ** TwilioVoiceService.reRegisterAttempt));
        TwilioVoiceService.reRegisterAttempt += 1;
        logger.info(`${LogTitle} re-register waiting on a fresh Twilio JWT`, LogTitle, { userId, reason, delay });
        TwilioVoiceService.reRegisterTimer = setTimeout(() => {
          TwilioVoiceService.reRegisterTimer = null;
          void TwilioVoiceService.refreshExpiredTwilioToken();
        }, delay);
        return;
      }
      if (!NetworkAvailability.isOnline()) {
        appDebugWarn(`${LogTitle} attemptReRegistration deferred — offline`, { userId, reason });
        if (!TwilioVoiceService.pendingOnlineReRegistration) {
          TwilioVoiceService.pendingOnlineReRegistration = true;
          NetworkAvailability.onceOnline(() => {
            TwilioVoiceService.pendingOnlineReRegistration = false;
            TwilioVoiceService.scheduleReRegistration('online');
          });
        }
        return;
      }
      if (TwilioVoiceService.hasActiveCallLeg() || TwilioVoiceService.callConnection) {
        logger.info(`${LogTitle} Skipping re-registration — active call leg`, LogTitle, { userId, reason });
        return;
      }
      if (TwilioVoiceService.reRegisterTimer) return;
      const delay = Math.min(30_000, 2_000 * (2 ** TwilioVoiceService.reRegisterAttempt));
      logger.info(`${LogTitle} re-register scheduled`, LogTitle, {
        userId,
        reason,
        delay,
        attempt: TwilioVoiceService.reRegisterAttempt,
      });
      TwilioVoiceService.reRegisterTimer = setTimeout(() => {
        TwilioVoiceService.reRegisterTimer = null;
        TwilioVoiceService.attemptReRegistration();
      }, delay);
    };

    static attemptReRegistration = () => {
      const userId = store.getState()?.auth?.authUser?.userId;
      if (TwilioVoiceService.reRegisterTimer) {
        clearTimeout(TwilioVoiceService.reRegisterTimer);
        TwilioVoiceService.reRegisterTimer = null;
      }
      if (TwilioVoiceService.handingOff || !isVoiceTabLeader()) return;
      if (TwilioVoiceService.tokenRefreshRequired) {
        void TwilioVoiceService.refreshExpiredTwilioToken();
        return;
      }
      if (TwilioVoiceService.hasActiveCallLeg() || TwilioVoiceService.callConnection) {
        logger.info(`${LogTitle} Skipping re-registration — active call leg`, LogTitle, { userId });
        return;
      }
      if (!NetworkAvailability.isOnline()) {
        TwilioVoiceService.scheduleReRegistration('offline');
        return;
      }
      if (TwilioVoiceService.registerInFlight) {
        return;
      }
      const device = TwilioVoiceService.twilioDevice;
      if (!device || device.state === 'destroyed' || device.state !== 'unregistered') {
        return;
      }
      TwilioVoiceService.reRegisterAttempt += 1;
      TwilioVoiceService.registerInFlight = true;
      logger.info(`${LogTitle} Trying to re-register for userId: ${userId}, Twilio Device state: ${device.state}`);
      Promise.resolve(device.register()).then(() => {
        TwilioVoiceService.reRegisterAttempt = 0;
      }).catch((error) => {
        const described = TwilioVoiceService.describeTwilioError(error);
        logger.error(`${LogTitle} Failed to re-register for userId: ${userId}`, LogTitle, described);
        if (TwilioVoiceService.isExpiredAccessTokenError(error) || described.errorCode === 20101) {
          TwilioVoiceService.noteRegistrationTrouble(
            'trouble',
            'Having trouble registering your phone. Refreshing your session…',
          );
          void TwilioVoiceService.refreshExpiredTwilioToken();
          return;
        }
        if (TwilioVoiceService.reRegisterAttempt >= 4) {
          TwilioVoiceService.reRegisterAttempt = 0;
          TwilioVoiceService.noteRegistrationTrouble(
            'failed',
            'Phone registration failed. Refresh the page to try again.',
          );
          void TwilioVoiceService.fetchAndUpdateTwilioToken('reRegister-exhausted');
          return;
        }
        TwilioVoiceService.noteRegistrationTrouble(
          'trouble',
          'Having trouble registering your phone. Retrying…',
        );
        TwilioVoiceService.scheduleReRegistration('register-rejected');
      }).finally(() => {
        TwilioVoiceService.registerInFlight = false;
      });
    };

  static destroy() {
    // We need to make sure the offline handler doesn't get triggered when destroying the device
    // TwilioDevice.off('offline', TwilioVoiceService.offlineHandler);
    // TwilioDevice.destroy();
    // store.dispatch(resetTwilio());
  }

  static safeDestroy() {
    try {
      // // We need to make sure the offline handler doesn't get triggered when destroying the device
      // TwilioDevice.off('offline', TwilioVoiceService.offlineHandler);
      // TwilioDevice.destroy();

    } catch (error) {
      logger.error(`${LogTitle} Failed to destroy Twilio Device`, LogTitle, { error });
    }

    // store.dispatch(resetTwilio());
  }

  /**
   * Initiates a connection to the caller number that is specified in the connect params.
   * @static
  */
  static async connect(user: any, connectParams: any) {
    TwilioVoiceService.ensureVoiceLeader();
    if (!isVoiceTabLeader()) {
      TwilioVoiceService.pendingConnectParams = connectParams;
      const becameLeader = await requestVoiceLeadership();
      if (!becameLeader && !isVoiceTabLeader()) {
        TwilioVoiceService.pendingConnectParams = null;
        logger.warn(`${LogTitle} outbound blocked — another tab holds the phone line`, LogTitle, {
          userId: user?.userId || user?.id,
        });
        store.dispatch(showSnackbar({
          snackbarType: SnackbarType.Error,
          snackbarData: { message: 'Another browser tab is on a call. End that call before dialing from this tab.' },
        }));
        return;
      }
      if (!TwilioVoiceService.twilioDevice && TwilioVoiceService.pendingVoiceSetup) {
        const pending = TwilioVoiceService.pendingVoiceSetup;
        await TwilioVoiceService.setupTwilio(pending.twilioToken, pending.effectiveRegionId, pending.callStateToRestore);
      }
      return;
    }

    // Check if Twilio is registered
    if (!TwilioVoiceService.twilioDevice || TwilioVoiceService.twilioDevice.state !== 'registered') {
        TwilioVoiceService.attemptReRegistration();
        TwilioVoiceService.pendingConnectParams = connectParams;
        return; // Abort the call
    }
    TwilioVoiceService.pendingConnectParams = null; // Reset pendingConnectParams

    if (connectParams) {
        try {
            const callRecordId = connectParams?.callRecordId || `${uuidv4()}__${user?.id}`;
            const callForwardChainReference = connectParams?.callForwardChainReference || uuidv4();

            // Drop the first call if an active call exists
            if (TwilioVoiceService.callConnection) {
                TwilioVoiceService.callConnection.reject();
                TwilioVoiceService.callConnection = null;
            }

            // Convert object keys to Pascal Case for Twilio parameters
            const params = {
                From: connectParams.fromNumber,
                FromId: connectParams.fromId,
                To: connectParams.toNumber,
                ToId: connectParams.toId,
                TenantCode: connectParams.tenantCode,
                CorrelationToken: uuidv4(),
                CallRecordId: callRecordId,
                CallForwardChainReference: callForwardChainReference,
                ContactId: connectParams.toId,
                ContactType: connectParams.contactType || ContactTypeEnum.unknown,
                ContactFirstName: connectParams.contactFirstName,
                ContactLastName: connectParams.contactLastName,
                AssignedTeamId: connectParams.assignedTeamId,
                TraversedRoutes: connectParams.traversedRoutes,
                CallEventId: connectParams.callEventId,
                CampaignId: connectParams.campaignId,
                CallNote: connectParams.callNote,
            };

            TwilioVoiceService.callConnection = await TwilioVoiceService.twilioDevice.connect({ params });
            noteVoiceLeaderCallActive(true);
            TwilioVoiceService.callConnection.on("accept", TwilioVoiceService.acceptHandler);
            TwilioVoiceService.callConnection.on("disconnect", TwilioVoiceService.disconnectHandler);
            TwilioVoiceService.callConnection.on("cancel", TwilioVoiceService.disconnectHandler);
            TwilioVoiceService.callConnection.on("ringing", TwilioVoiceService.ringingHandler);
            TwilioVoiceService.attachCallSdkDiagnostics(TwilioVoiceService.callConnection, 'outbound');

            store.dispatch(updateCallBarState(CallBarState.CALLING));
            TwilioVoiceService.updateUserAvailableStatus(callRecordId, true);

            const callingContact = store.getState()?.callingContact?.data;
            let contactRemoteId = connectParams?.contactRemoteId || callingContact?.contactRemoteId || callingContact?.remoteId;

            if (contactRemoteId) {
                SaleforceService.navigateToObject(contactRemoteId);
            } else {
                SaleforceService.callingObjectNavigation(connectParams?.toNumber, connectParams?.toId, true);
            }

            store.dispatch(addLastCallRecord({
                callRecordId,
                content: { callDirection: CallActivityDirectionEnum.outgoing, callMedium: CallMedium.salesForce },
                contactDetails: callingContact
            }));
            logger.info(`${LogTitle} User:${user?.id} Outbound call init for callid ${callRecordId}`);
            setTimeout(() => SaleforceService.publishObject({ callId: callRecordId, type: 'Connect' }, CALLCONNECTIONMC), 2000);
        } catch (exception) {
            logger.error(`${LogTitle} User:${user?.id} Twilio connect error:`, LogTitle, {exception});
            store.dispatch(showSnackbar({
                snackbarType: SnackbarType.Error,
                snackbarData: { message: "Failed to connect call. Please try again." }
            }));
        }
    }
}

static async checkAudioPermissions(): Promise<boolean> {
  const userId = store.getState()?.auth?.authUser?.userId;

  try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop()); // Release the stream
      return true;
  } catch (error) {
      logger.error(`${LogTitle} User: ${userId} Microphone permission denied:`,LogTitle,{ error});
      return false;
  }
}

/*
  static async connect(user: any, connectParams: any) {
    const isOnline = store.getState()?.networkConnection?.connection;

    if (!isOnline) {
      const msg = 'Unable to place the call. Please check your internet connection and try again.';
      console.error(msg);
      store.dispatch(showSnackbar({ snackbarType: SnackbarType.Error, snackbarData:{ message: msg} }));
      return; // Abort the call
    }

    if (TwilioVoiceService.twilioDevice.state !== 'registered') {
      TwilioVoiceService.attemptReRegistration();
      TwilioVoiceService.pendingConnectParams = connectParams;
      return; // Abort the call
    }

    TwilioVoiceService.pendingConnectParams = null; // reset pendingConnectParams value

    if (connectParams) {
      try {
        const callRecordId = connectParams?.callRecordId ? connectParams?.callRecordId :
          `${uuidv4()}__${user?.id}`;
        const callForwardChainReference = connectParams?.callForwardChainReference ? connectParams?.callForwardChainReference :
          uuidv4();
        
        // TODO: We probably shouldn't be disconnecting an incoming call. Maybe hold it?
        // if there is an incoming call disconnect it.
        if (TwilioVoiceService.callConnection) {
          // @ts-ignore
          TwilioVoiceService.callConnection.reject();
          TwilioVoiceService.callConnection = null;
        }

        // convert the object keys to pascal case.
        const params = {
          From: connectParams.fromNumber,
          FromId: connectParams.fromId,
          To: connectParams.toNumber,
          ToId: connectParams.toId,
          TenantCode: connectParams.tenantCode,
          CorrelationToken: uuidv4(),
          CallRecordId: callRecordId,
          CallForwardChainReference: callForwardChainReference,
          ContactId: connectParams.toId,
          ContactType: connectParams.contactType || ContactTypeEnum.unknown,
          ContactFirstName: connectParams.contactFirstName,
          ContactLastName: connectParams.contactLastName,
          AssignedTeamId: connectParams.assignedTeamId,
          TraversedRoutes: connectParams.traversedRoutes,
          CallEventId: connectParams.callEventId, // Call from dialler
          CampaignId: connectParams.campaignId, // Call from dialler
          CallNote: connectParams.callNote,
        };

        TwilioVoiceService.callConnection = await TwilioVoiceService.twilioDevice.connect({ params });
        noteVoiceLeaderCallActive(true);
        TwilioVoiceService.callConnection.on("accept", TwilioVoiceService.acceptHandler);
        TwilioVoiceService.callConnection.on("disconnect", TwilioVoiceService.disconnectHandler);
        TwilioVoiceService.callConnection.on("cancel", TwilioVoiceService.disconnectHandler);
        TwilioVoiceService.callConnection.on("ringing", TwilioVoiceService.ringingHandler);

        store.dispatch(updateCallBarState(CallBarState.CALLING));
        TwilioVoiceService.updateUserAvailableStatus(callRecordId, true);
        const callingContact = store.getState()?.callingContact?.data;
        let contactRemoteId = connectParams?.contactRemoteId;

        if (!contactRemoteId && callingContact) {
          contactRemoteId = callingContact?.contactRemoteId || callingContact?.remoteId;
        }

        if (contactRemoteId) {
          SaleforceService.navigateToObject(contactRemoteId);
        } else {
          SaleforceService.callingObjectNavigation(connectParams?.toNumber, connectParams?.toId, true);
        }

      const callRecordDetail = {callRecordId, content:{callDirection:CallActivityDirectionEnum.outgoing, callMedium: CallMedium.salesForce }, contactDetails: callingContact}
      store.dispatch(addLastCallRecord(callRecordDetail));

        // We are sending this message to liveTranscript lwc component.
        // So we have to make sure that component has been rendered (as liveTranscript renders when we connect the call) while sending this message.
        // So giving a timeout 2sec to pass the message.
        setTimeout(() => SaleforceService.publishObject({callId: callRecordId ,type: 'Connect'}, CALLCONNECTIONMC), 2000);
      } catch (exception) {
        logger.error(`${LogTitle} connect for ${user?.id} got exception: `, LogTitle, { exception });
        store.dispatch(callError(exception));
      }
    } else {
      // store.dispatch(callError('Connection params cannot be null'));
    }
  }
*/
  static createOutboundCallConnectParams =
    (
      user: any, userNumber: any,
      contact: any, contactNumber: any, assignedTeamId: any,
      callEventId = null, campaignId = null, callRecordId = null, callForwardChainReference = null, callNote = '',
    ) => ({
      callRecordId,
      callForwardChainReference,
      fromId: user?.id,
      fromName: user?.firstName,
      fromNumber: userNumber,
      toName: ContactService.getContactFullName(contact),
      toId: contact?.contactId || contact?.id,
      tenantCode: user?.organisationDetails?.tenantCode,
      toNumber: contactNumber,
      contactRemoteId: contact?.RemoteId,
      contactFirstName: contact?.firstName,
      contactLastName: contact?.lastName,
      contactType: contact?.contactType,
      assignedTeamId,
      // Call event stuff
      callEventId,
      campaignId,
      callNote,
    })

  /**
  * Gets the active on going connection for the device.
  * @static
  */
  static getActiveConnection = () => {
    return TwilioVoiceService.callConnection || null;
  }

  /**
  * Send DTMF digits.
  * @static
  */
  static sendDigits = (digits: any) => {
    const connection = TwilioVoiceService.getActiveConnection();

    if (connection) {
      // @ts-ignore
      connection.sendDigits(digits);
      return true;
    }

    return false;
  }

  /**
  * Mute mic
  * @static
  */
  static muteMic = (muteState: boolean) => {
    const connection = TwilioVoiceService.getActiveConnection();

    if (connection) {
      // @ts-ignored
      connection.mute(muteState);
    }
  }

  /**
  * Terminates the active connection. This will trigger the disconnect event handler.
  * @static
  */
  static disconnect = () => {
    const connection = TwilioVoiceService.getActiveConnection();
    const userId = store.getState()?.auth?.authUser?.userId;

    if (connection) {
      TwilioVoiceService.markUserInitiatedDisconnect('TwilioVoiceService.disconnect');
      // @ts-ignored
      connection.disconnect();
      logger.info(`${LogTitle} userId: ${userId} disconnect: connection (user-initiated)`, LogTitle, {
        source: 'TwilioVoiceService.disconnect',
        callSid: connection?.parameters?.CallSid,
      });
    } else {
      logger.info(`${LogTitle} userId: ${userId} disconnect: connection null or undefine`);

      const disconnectAll = TwilioVoiceService.twilioDevice.disconnectAll();
      
      if (!disconnectAll) {
        logger.info(`${LogTitle} userId: ${userId} disconnect: disconnectAll failed`);

        if (TwilioVoiceService.twilioDevice.state !== 'Ready') {
          logger.info(`${LogTitle} userId: ${userId} disconnect: twilioDevice not Ready: ${TwilioVoiceService.twilioDevice.state}`);

          TwilioVoiceService.twilioDevice.destroy();
          const twilioToken = store.getState()?.twilio?.data?.token;
          const effectiveRegionId = store.getState()?.twilio?.data?.regionId;
          const restoringCallState = 'Ready';
          TwilioVoiceService.setupTwilio(twilioToken, effectiveRegionId);
        }
      }
    }
  }

  /**
   * Toggle recording pause. Note that pause recording won't work if recording has not started.
   *
   * @static
   * @param callRecordId
   */
  static pauseRecording(callRecordId: string, muted = true) {
    TwilioVoiceService.streamProcessManager.setStreamMuteState(callRecordId, muted);
  }

  /**
   * Create RTT socket connection and begin streaming.
   *
   * @static
   * @param callRecordId
   */
  static startRttStreaming(callRecordId: string, twilioCall?: any) {
    const userId = store.getState()?.auth?.authUser?.userId;
    logger.info('Rtt: startRttStreaming invoked', LogTitle, {
      callRecordId,
      userId,
      hasCallRef: !!twilioCall,
      staticHasCall: !!TwilioVoiceService.callConnection,
    });
    TwilioVoiceService.streamProcessManager.startConnection(callRecordId, twilioCall);
  }

  // Twilio Call back handler
  static readyHandler = (callStateToRestore: string = CallDeviceStatusEnum.Ready) => {
    store.dispatch(callStatusChange({ status: callStateToRestore }));
  }

  /**
   * This is triggered when a connection is opened, whether initiated using .connect() or via an accepted incoming connection.
   * @static
   */
  static acceptHandler = (connection: any) => {
    // Keep static ref in sync with the Call that raised "accept" — StreamProcessService and
    // _sendMetadata use TwilioVoiceService.callConnection; a stale null loses phone/mic stream + metadata.
    if (connection) {
      TwilioVoiceService.callConnection = connection;
      noteVoiceLeaderCallActive(true);
    }
    finishRemoteIncoming();

    const callDirection = TwilioVoiceService.getCallDirection(connection);
    const isIncoming = callDirection === CallActivityDirectionEnum.incoming;
    const callDetails = TwilioVoiceService.getCallDetailsFromConnection(connection);
    const callRecordId = callDetails?.callRecordId;
    const userId = store.getState()?.auth?.authUser?.userId;
    const callingSFContact = store.getState()?.callingContact?.data;
    const sfLookupRemoteId = callingSFContact?.remoteId;
    SaleforceService.getTasksAndBookmarksByLookupId(sfLookupRemoteId, callRecordId);

    logger.info(`${LogTitle} userId:${userId} acceptHandler call direction: ${callDirection} callid:${callRecordId} call Status: ${connection.status()}`)

    // Check if this is Agent 2 getting the call connection event
    if (this.isConnectedCallHotTransferAgent(connection)) {
      // In a hot transfer, the second connection (with agent) should be handled differently
      // What we are doing here is recording the call connected event
      // CallHotTransferService.hotTransferAgentConnectedSequence();
      return;
    }

    // If this is a resuming call, we need to make sure we clear the queued call details
    if (TwilioVoiceService.wasCallOnHold(connection)) {
      const currentActiveCallRecordId = store.getState()?.callCentre?.currentActiveCallRecordId;
      if (currentActiveCallRecordId && currentActiveCallRecordId === callRecordId) {
        logger.info(`${LogTitle} acceptHandler: wasCallOnHold`, LogTitle, { userId, currentActiveCallRecordId, callRecordId });

        store.dispatch(clearResumingCallDetails());
        TwilioVoiceService.streamProcessManager.receiveCallHoldUpdate(callRecordId, false);
      }

      return;
    }

    // Start RTTP streaming
    CallActivityService.resolveCallInProgressStatusChange({ id: callRecordId, twilioCall: connection });
   
    if (isIncoming) {
      TwilioVoiceService.updateUserAvailableStatus(callRecordId, true);

      const callingContact = store.getState()?.callingContact?.data;
      const callRecordDetail = {callRecordId, content:{callDirection:callDetails?.callDirection, callMedium: CallMedium.salesForce }, contactDetails: callingContact}
      store.dispatch(addLastCallRecord(callRecordDetail));
    }

    // Future use, if we want to show animation with volume info
    // TwilioVoiceService.callConnection.on("volume", TwilioVoiceService.volumeHandler);

    // // Do the needful setups for the call
    store.dispatch(updateCurrentActiveCallRecordId(callDetails?.callRecordId));
    store.dispatch(setCallPrimaryMetadata(
      {
        previousCallRecordId: callDetails?.previousCallRecordId,
        callRecordId: callDetails?.callRecordId,
        contactNumber: callDetails?.contactNumber,
        customerHasJoinedConference: callDetails?.customerHasJoinedConference,
        isHotTransfer: callDetails?.isHotTransfer,
        isConference: callDetails?.isConference,
        metadata: callDetails,
      }
    ));
  }

  /**
     * Emitted when any device error occurs. These may be errors in your request, your token, connection errors,
     * or other application errors.
     *
     * See the Twilio Client error code reference for more information. https://www.twilio.com/docs/client/errors
     * @static
     */
  static errorHandler = async(error: any, call: any) => {
    const userId = store.getState()?.auth?.authUser?.userId;
    const isOnline = NetworkAvailability.isOnline();
    const onActiveCall = TwilioVoiceService.hasActiveCallLeg();
    const errorCode = TwilioVoiceService.getTwilioErrorCode(error);
    logger.info(`${LogTitle} UserId: ${userId} errorHandler: `, LogTitle, {
      ...TwilioVoiceService.describeTwilioError(error),
      onActiveCall,
      errorCode,
    });

    if (TwilioVoiceService.handingOff || !isVoiceTabLeader()) {
      return;
    }

    // 31005 / 31009 / 31204 are a dead websocket, not a bad JWT. updateToken here caused the 20101 loop.
    // 31205 is the opposite: the JWT itself is expired, so registering again with it cannot succeed.
    if (TwilioVoiceService.isExpiredAccessTokenError(error)) {
      if (Date.now() < TwilioVoiceService.ignoreInvalidTokenUntil) {
        return;
      }
      logger.warn(`${LogTitle} Twilio JWT rejected — fetching a new token before the next register`, LogTitle, {
        userId,
        errorCode,
      });
      TwilioVoiceService.noteRegistrationTrouble(
        'trouble',
        'Having trouble registering your phone. Refreshing your session…',
      );
      if (onActiveCall) {
        TwilioVoiceService.scheduleDeferredTokenRefresh('jwt-expired');
        return;
      }
      void TwilioVoiceService.refreshExpiredTwilioToken();
      return;
    }

    if (TwilioVoiceService.isSignalingTransportError(error)) {
      if (onActiveCall) {
        logger.warn(`${LogTitle} Signaling blip during active call — waiting for SDK reconnect`, LogTitle, {
          userId,
          errorCode,
        });
        return;
      }
      // Each 31009 makes the SDK retry presence on its own timer. Do not start another
      // register on top of that, and do not register at all while the browser is offline.
      if (!isOnline) {
        const device = TwilioVoiceService.twilioDevice;
        if (device && (device.state === 'registering' || device.state === 'registered')) {
          Promise.resolve(device.unregister()).catch(() => undefined);
        }
      }
      if (TwilioVoiceService.registerInFlight || TwilioVoiceService.reRegisterTimer) {
        return;
      }
      TwilioVoiceService.scheduleReRegistration('signaling');
      return;
    }

    if (!onActiveCall || !TwilioVoiceService.isRecoverableTokenError(error)) {
      store.dispatch(callStatusChange({ status: CallDeviceStatusEnum.Error }));
    } else {
      logger.warn(`${LogTitle} Token/signaling error during active call — refresh only`, LogTitle, {
        userId,
        errorCode,
      });
    }

    if (!isOnline) {
      if (!TwilioVoiceService.pendingOnlineReRegistration) {
        TwilioVoiceService.pendingOnlineReRegistration = true;
        NetworkAvailability.onceOnline(() => {
          TwilioVoiceService.pendingOnlineReRegistration = false;
          TwilioVoiceService.scheduleReRegistration('online');
        });
      }
      return;
    }

    if (TwilioVoiceService.shouldSkipTokenRefreshForEarlyInboundLeg()) {
      return;
    }

    if (TwilioVoiceService.isRecoverableTokenError(error)) {
      if (onActiveCall) {
        TwilioVoiceService.scheduleDeferredTokenRefresh('errorHandler');
      } else {
        await TwilioVoiceService.fetchAndUpdateTwilioToken('errorHandler');
      }
      return;
    }

    if (errorCode === 31208) {
      store.dispatch(showSnackbar({
        snackbarType: SnackbarType.Error,
        snackbarData: { message: "Failed, RocketPhone Call is not supported in this browser.." }
      }));
      return;
    }

    if (onActiveCall) {
      logger.warn(`${LogTitle} Skipping re-registration during active call`, LogTitle, { userId, errorCode });
      return;
    }

    TwilioVoiceService.scheduleReRegistration('errorHandler');
  };

  /**
  * This is triggered when the connection to Twilio drops or the device's token is invalid/expired.
  * @static
  */
  static offlineHandler = () => {
    // re initialize the device since its offline
    // store.dispatch(setupTwilio());
    store.dispatch(getTwilioData(store.getState()?.auth?.authUser?.userId));
  };

  /**
  * This is triggered when the connection to Twilio drops or the device's token is invalid/expired.
  * @static
  */
  static volumeHandler = (inputVolume: any, outputVolume: any) => {
    //console.log(LogTitle, "volumeHandler: ", { inputVolume, outputVolume});
  };

  /**
   * This is triggered whenever an incoming connection is made.
   * The handler function receives a Twilio.Connection object as an argument.
   * This connection will be in state pending until you call .accept() on it.
   * @static
   */
  static showIncomingPanel(payload: any) {
    presentIncomingCall(payload);
  }

  static incomingHandler = (connection: any) => {
    store.dispatch(getCallIncoming());
    const currentActiveCallRecordId = store.getState()?.callCentre?.currentActiveCallRecordId;
    const incomingCallInfoData = store.getState()?.callCentre?.incomingCallInfo;
    const userId = store.getState()?.auth?.authUser?.userId;

    const callDetails = TwilioVoiceService.getCallDetailsFromConnection(connection);
    const callRecordId = callDetails?.callRecordId;
    const existingcallDetails = incomingCallInfoData?.callDetails;
    const existingCallrecordId = existingcallDetails?.callRecordId;

    logger.info(`${LogTitle} UserId: ${userId} CallID: ${callRecordId} incomingHandler: `, LogTitle, {
      customParameters: metaFromTwilioCustomParameters(connection?.customParameters),
    });

    if ((existingcallDetails && existingCallrecordId !== callRecordId) ||
      (TwilioVoiceService.wasCallOnHold(connection) && currentActiveCallRecordId !== callRecordId)) {
      connection.ignore();
      logger.info(`${LogTitle} UserId: ${userId} incomingHandler: ignore`, LogTitle, { currentActiveCallRecordId, callRecordId})

      return;
    }

    if (!!currentActiveCallRecordId && currentActiveCallRecordId !== callRecordId) {
      logger.info(`${LogTitle} UserId: ${userId} incomingHandler: disconnect`,LogTitle, { currentActiveCallRecordId, callRecordId})
      TwilioVoiceService.markAppInitiatedDisconnect('incomingHandler-replace-active-call');
      connection.disconnect();

      return;
    }

    TwilioVoiceService.callConnection = connection;
    noteVoiceLeaderCallActive(true);
    TwilioVoiceService.callConnection.on("disconnect", TwilioVoiceService.disconnectHandler);
    TwilioVoiceService.callConnection.on("cancel", () => TwilioVoiceService.cancelHandler(TwilioVoiceService.callConnection));
    TwilioVoiceService.callConnection.on("reject", () => TwilioVoiceService.cancelHandler(TwilioVoiceService.callConnection));
    TwilioVoiceService.callConnection.on("accept", TwilioVoiceService.acceptHandler);
    TwilioVoiceService.attachCallSdkDiagnostics(connection, 'incoming');

    // If this is a resuming call, we need to make sure we clear the queued call details
    if (TwilioVoiceService.wasCallOnHold(connection)) {
      if (currentActiveCallRecordId && currentActiveCallRecordId === callRecordId) {
        logger.info(`${LogTitle} UserId: ${userId} incomingHandler: wasCallOnHold and accept()`,LogTitle, { currentActiveCallRecordId, callRecordId })
        connection.accept();
        return;
      }
    }

    // Execute call incoming sequence
    const incomingCallType = this.getIncomingCallType(connection);
    const { firstName, lastName } = store.getState()?.auth?.user;
    const assignedTeam = store.getState()?.auth?.userTeam;
    const hasTeam = !!(assignedTeam?.id === callDetails?.assignedTeamId);
    const hasHuntGroup = !!callDetails?.traversedRoutes?.[1];
    let displayName = `${firstName} ${lastName}`;

    if (hasTeam) {
      if (hasHuntGroup) {
        displayName = `\n${callDetails?.traversedRoutes[0]?.RouteCode} (${callDetails?.traversedRoutes[0]?.RouteName}) > ${callDetails?.traversedRoutes[1]?.RouteCode} (${callDetails?.traversedRoutes[1]?.RouteName})`;
      } else {
        displayName = `${assignedTeam?.route} (${assignedTeam?.name})`;
      }
    }

    const connectionContact = [{
      contactType: callDetails?.contactType,
      phone: callDetails?.contactNumber,
      firstName: callDetails?.contactName,
      remoteId: callDetails?.contactRemoteId
    }];

    let contactSearchByPhoneCompleted = false;

    SaleforceService.getContactSearchByPhone(callDetails?.contactNumber)
      .then((result) => {
        contactSearchByPhoneCompleted = true;

        if (TwilioVoiceService.callConnection) {
          TwilioVoiceService.showIncomingPanel({ contacts: result || [], incomingCallType, callDetails, contactFromConnection: false });
          let potentialContact;
          const contactRemoteId = callDetails?.contactRemoteId;
      
          if (result?.length > 0) {
            potentialContact = result?.[0];
          }

          CallNotificationService.showIncomingCallNotification(callDetails, potentialContact, displayName);
        }
      })
      .catch((error) => {
        appDebugLog('getContactSearchByPhone error', error);
        contactSearchByPhoneCompleted = true;

        if (TwilioVoiceService.callConnection) {
          TwilioVoiceService.showIncomingPanel({ incomingCallType, callDetails, contactFromConnection: false });
          CallNotificationService.showIncomingCallNotification(callDetails, null, displayName);
        }
      });

    if (!contactSearchByPhoneCompleted) {
      TwilioVoiceService.showIncomingPanel({ contacts: connectionContact || [], incomingCallType, callDetails, contactFromConnection: true });
      CallNotificationService.showIncomingCallNotification(callDetails, connectionContact, displayName);
    }

    SaleforceService.setSoftPhoneVisibleIfNot();
  }

  static ringingHandler = (hasEarlyMedia : any) => {
    const userId = store.getState()?.auth?.authUser?.userId;

    logger.info(`${LogTitle} ringingHandler: `, LogTitle, { userId, hasEarlyMedia  })
    store.dispatch(updateCallBarState(CallBarState.IN_CALL));
  };

  static updateUserAvailableStatus = (callRecordId: string | null, isOnCall: boolean) => {
    const userId =
      store.getState()?.auth?.user?.id || store.getState()?.auth?.authUser?.userId;
    if (!userId || userId === 'null' || userId === 'undefined') {
      appDebugWarn(`${LogTitle} updateUserAvailableStatus skipped — missing userId`, {
        callRecordId,
        isOnCall,
      });
      return;
    }

    const tenantUserStatusesState = store.getState()?.settings?.tenantSettings?.userStatuses;
    let userStatusId = null;
    let isAvailable = true;
    
    if (isOnCall) {
      const onCallUserStatus = tenantUserStatusesState?.filter((item: any) => item?.name === DefaultUserStatuses.OnCall)?.[0];
      userStatusId = onCallUserStatus?.id;
      isAvailable = false;
    } else {
      const defaultAvailableStatus = tenantUserStatusesState?.filter((item: any) => item?.name === DefaultUserStatuses.Available)?.[0];
      userStatusId = defaultAvailableStatus?.id;
    }

    if (!userStatusId) {
      appDebugWarn(`${LogTitle} updateUserAvailableStatus skipped — missing UserStatusId`, {
        userId,
        isOnCall,
      });
      return;
    }
    
    const userStatuses = { Available: isAvailable, UserStatusId: userStatusId, ConnectedCallId: callRecordId };
    store.dispatch(updateUserStatus(userStatuses));
  }

  /**
   * This is triggered when an incoming connection is canceled by the caller before it is accepted by the Twilio Client device.
   * @static
   */
  static cancelHandler = (connection: any) => {
    const userId = store.getState()?.auth?.authUser?.userId;
    const currentActiveCallRecordId = store.getState()?.callCentre?.currentActiveCallRecordId;
    const cancelledCallRecordId = TwilioVoiceService.getCallDetailsFromConnection(connection)?.callRecordId;
    const incomingCallRecordId = TwilioVoiceService.getCallDetailsFromConnection(TwilioVoiceService.callConnection)?.callRecordId;

    if (!!currentActiveCallRecordId && currentActiveCallRecordId !== cancelledCallRecordId) {
      logger.warn(`${LogTitle} userId: ${userId} CallID: ${cancelledCallRecordId} cancelHandler, ignor data:`, LogTitle, { userId, currentActiveCallRecordId, cancelledCallRecordId });
      return;
    }

    logger.info(`${LogTitle} userId: ${userId} CallID: ${cancelledCallRecordId} cancelHandler: `, LogTitle, {
      customParameters: metaFromTwilioCustomParameters(connection?.customParameters),
    });

    // update the last connection.
    TwilioVoiceService.callConnection = null;
    noteVoiceLeaderCallActive(false);
    finishRemoteIncoming();

    // Execute the cancel sequence
    CallNotificationService.hideIncomingCallNotification(incomingCallRecordId);
    // Rewrite the status the UI is already showing. A cancelled ring must not leave
    // beacon without a dialable row. Do not force the default Available status.
    TwilioVoiceService.syncShownAvailabilityToRedis('call-cancelled', true);
    SaleforceService.publishObject({callId: currentActiveCallRecordId, type: 'Cancel'}, CALLCONNECTIONMC);

    // We have to hide the incoming call panel
    store.dispatch(clearCallingContact());
    store.dispatch(cleanupContactSearchByPhone());
    store.dispatch(setIncomingCallIdle());

    void TwilioVoiceService.applyPendingTokenRefresh();
  };

  /**
   * Fired any time a Connection is closed. The handler function receives the Twilio.Connection object that was
   * just closed as an argument.
   * @static
   */
  /**
   * Real hang-up releases the mic. Hold also disconnects this leg, but stopping the
   * input tracks there leaves them `ended` for the resumed call (RTT then sends silence).
   */
  static releaseDeviceInputStream = () => {
    const audio = TwilioVoiceService.twilioDevice?.audio;
    const candidates = [audio?.input?.stream, audio?.inputStream];
    const seen = new Set<MediaStream>();
    for (const candidate of candidates) {
      if (!(candidate instanceof MediaStream) || seen.has(candidate)) continue;
      seen.add(candidate);
      candidate.getTracks().forEach((track: MediaStreamTrack) => {
        try { track.stop(); } catch { /* already ended */ }
      });
    }
  };

  /**
   * setInputDevice is a no-op while a dead stream is still attached to the same device id.
   * Drop it, then open the mic again. Falls back to getUserMedia when the SDK cannot.
   */
  static async recoverMicrophoneInput(reason: string): Promise<{ stream: MediaStream | null; owned: boolean }> {
    const userId = store.getState()?.auth?.authUser?.userId;
    const audio = TwilioVoiceService.twilioDevice?.audio;
    if (audio && typeof audio.setInputDevice === 'function') {
      const deviceId = TwilioVoiceService.microphoneDeviceId(audio);
      try {
        if (typeof audio.unsetInputDevice === 'function') {
          await audio.unsetInputDevice();
        }
        await audio.setInputDevice(deviceId);
        const stream = TwilioVoiceService.deviceInputStream(audio);
        const live = !!stream?.getAudioTracks().some((track: MediaStreamTrack) => track.readyState === 'live');
        logger.info(`${LogTitle} microphone input reacquired`, LogTitle, { userId, reason, deviceId, live });
        if (live && stream) return { stream, owned: false };
      } catch (error) {
        logger.error(`${LogTitle} microphone reacquire via Twilio failed`, LogTitle, {
          userId,
          reason,
          deviceId,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      logger.info(`${LogTitle} microphone reacquired via getUserMedia`, LogTitle, { userId, reason });
      return { stream, owned: true };
    } catch (error) {
      logger.error(`${LogTitle} microphone reacquire failed`, LogTitle, {
        userId,
        reason,
        error: error instanceof Error ? error.message : String(error),
      });
      return { stream: null, owned: false };
    }
  }

  private static microphoneDeviceId = (audio: any): string => {
    const current = audio.inputDevice?.deviceId;
    if (typeof current === 'string' && current) return current;
    const devices = audio.availableInputDevices;
    if (devices && typeof devices.keys === 'function') {
      const first = devices.keys().next();
      if (!first.done && first.value) return first.value;
    }
    return 'default';
  };

  private static deviceInputStream = (audio: any): MediaStream | null => {
    const stream = audio.inputStream || audio.input?.stream;
    return stream instanceof MediaStream ? stream : null;
  };

  static disconnectHandler = (connection: any) => {
    const callsOnHoldData = store.getState()?.callState?.callsOnHold;
    const currentActiveCallRecordId = store.getState()?.callCentre?.currentActiveCallRecordId;

    // If it's a held call, we don't have to do antyhing
    // Redux Store Call Hold state check
    const callDetails = TwilioVoiceService.getCallDetailsFromConnection(connection);
    const callRecordId = callDetails?.callRecordId;
    const userId = store.getState()?.auth?.authUser?.userId;
    const { initiator: disconnectInitiator, source: disconnectSource } =
      TwilioVoiceService.resolveDisconnectInitiator();
    const callStatus = typeof connection?.status === 'function' ? connection.status() : 'unknown';
    const disconnectReason = connection.getError?.() ?? null;

    logger.info(`${LogTitle} userId: ${userId} CallID: ${callRecordId} disconnectHandler`, LogTitle, {
      disconnectInitiator,
      disconnectSource,
      callStatus,
      callSid: connection?.parameters?.CallSid,
      disconnectReason,
      customParameters: metaFromTwilioCustomParameters(connection?.customParameters),
    });
    const callIsPutOnHold = !!(currentActiveCallRecordId && callRecordId === currentActiveCallRecordId && callsOnHoldData?.[callRecordId]);
    if (callIsPutOnHold) {
      logger.info(`${LogTitle} userId: ${userId} CallID: ${callRecordId} hold disconnect — keeping microphone input`, LogTitle, {
        callSid: connection?.parameters?.CallSid,
      });
      TwilioVoiceService.streamProcessManager.receiveCallHoldUpdate(callRecordId, true);
      return;
    }

    TwilioVoiceService.releaseDeviceInputStream();

    if (currentActiveCallRecordId && callRecordId === currentActiveCallRecordId) {

      const callingContact = store.getState()?.callingContact?.data;
      const callRecordDetail = {callRecordId, content:{callDirection:callDetails?.callDirection, callMedium: CallMedium.salesForce }, contactDetails: callingContact}
      store.dispatch(addLastCallRecord(callRecordDetail));

      const isCallDispositionNotMandatory = store.getState()?.settings?.tenantSettings?.tenantCallSettings?.isCallDispositionNotMandatory;
      if (isCallDispositionNotMandatory) {
        store.dispatch(updateCallBarState(CallBarState.IDLE));
      } else {
        store.dispatch(updateCallBarState(CallBarState.POST_CALL));
      }

      store.dispatch(clearCallingContact());
      TwilioVoiceService.updateUserAvailableStatus(null, false);
      store.dispatch(updateCurrentActiveCallRecordId(null));
      SaleforceService.publishObject({callId: callRecordId, type: 'Disconnect'}, CALLCONNECTIONMC);
    }

    // If we get here, it's a regualr as call
    // Going with the usual thingy
    // remove closed connections
    TwilioVoiceService.cleanupConnectionData(connection);

    // After disconnecting, we need to do some stuff to make the UI take effect
    this.postDisconnectCleanup();
  }


  /**
   * After the call disconnect is finished, the resets we need to do
  */
  static postDisconnectCleanup = () => {
    
    // Reset the device status back to "Ready"
    store.dispatch(callStatusChange({ status: CallDeviceStatusEnum.Ready }));

    const isCallDispositionNotMandatory = store.getState()?.settings?.tenantSettings?.tenantCallSettings?.isCallDispositionNotMandatory;
    if (isCallDispositionNotMandatory) {
      store.dispatch(updateCallBarState(CallBarState.IDLE));
    } else {
      store.dispatch(updateCallBarState(CallBarState.POST_CALL));
    }
    // store.dispatch(updateConnectedUserInitialDetails(null));
    TwilioVoiceService.callConnection = null;
    noteVoiceLeaderCallActive(false);

    void TwilioVoiceService.applyPendingTokenRefresh();
  }

  static deviceChangeHandler = () => {
    const userId = store.getState()?.auth?.authUser?.userId;

    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      logger.warn(`${LogTitle} Navigator is not available.`, LogTitle, {userId});
      return;
    }

    // const twilioSetupCompleted = callStateSelectors.twilioSetupCompleted(store.getState());

    navigator.mediaDevices.enumerateDevices()
      .then((devices) => {
        let isAudioInputAvailable = false;
        devices.forEach((device) => {
          if (device.kind === 'audioinput') {
            isAudioInputAvailable = true;
          }
        });
        // store.dispatch(updateCallAudioInputState(isAudioInputAvailable));

        // We destroy the twillio connection if the Mic is not connected
        // We have to dispatch setupTwillio if the Mic is connected again
        if (!isAudioInputAvailable) {
          //logger.info(`${LogTitle} Input device not available`, devices);
          //TwilioVoiceService.safeDestroy();
        }
        //  else if (isAudioInputAvailable && !twilioSetupCompleted) {
        //    store.dispatch(setupTwilio());
        // }
        //TwilioVoiceService.twilioDevice.audio.ringtoneDevices.set('c88d223096980c7df8e647fa2e13762c744d033c430c156cd2e301ea07e88308'); // Set active device
      })
      .catch((err) => {
        logger.info(`${LogTitle} ${err.name}: ${err.message}`, LogTitle, { userId, err });
      });
  }

  static tokenWillExpireHandler = async() => {
    const userId = store.getState()?.auth?.authUser?.userId;
    if (TwilioVoiceService.shouldSkipTokenRefreshForEarlyInboundLeg()) {
      logger.info(`${LogTitle} User:${userId} tokenWillExpireHandler: skip refresh (early inbound leg)`, LogTitle, {
        deviceState: TwilioVoiceService.twilioDevice?.state,
      });
      return;
    }
    if (!TwilioVoiceService.twilioDevice) {
      return;
    }

    if (!NetworkAvailability.isOnline()) {
      appDebugWarn(`${LogTitle} tokenWillExpireHandler deferred — offline`, { userId });
      if (!TwilioVoiceService.pendingOnlineTokenRefresh) {
        TwilioVoiceService.pendingOnlineTokenRefresh = true;
        NetworkAvailability.onceOnline(() => {
          TwilioVoiceService.pendingOnlineTokenRefresh = false;
          void TwilioVoiceService.fetchAndUpdateTwilioToken('tokenWillExpireHandler:online', { force: true });
        });
      }
      return;
    }

    await TwilioVoiceService.fetchAndUpdateTwilioToken('tokenWillExpireHandler');
  }

  /**
   * Clean up connection information
  */
  static cleanupConnectionData = (connection: any) => {
    // Remove closed connection from TwilioVoiceService class and socket mapping map
    const callDetails = TwilioVoiceService.getCallDetailsFromConnection(connection);
    const callRecordId = callDetails?.callRecordId;
    TwilioVoiceService.streamProcessManager.stopConnection(callRecordId);
  }

  // Helper Methods

  static wasCallOnHold = (connection: any) => connection.customParameters?.get('CallWasOnHold')?.toLowerCase() === 'true';

  /**
 * Says whether this is the following scenario (Agent 1 perspective)
 *  - Agent 1 hot transfers a call to Agent 2
 *  - Call connected to Agent 2
 */
  static isConnectedCallHotTransferAgent = (connection: any) => {
    // const { callRecordId } = TwilioVoiceService.getCallDetailsFromConnection(connection);

    return false;
    // return CallHotTransferService.isConnectedCallHotTransferAgent(callRecordId);
  }

  /**
   * Gets the correct CallSid (RemoteId) from the connection.
   *
   * The CallSid will change when a call is resumed after holding.
   * But we should still use the iriginal CallSid since it is the RemoteId used to create the CallRecord.
   * This function will automatically handle the conditional logic.
   *
   * @param   {any}     connection  The connection object given by Twilio SDK
   * @return  {string}              The RemoteId of the connection
   */
  static getCallRemoteId = (connection: any) => {
    const customRemoteId = connection?.customParameters?.get('CallRemoteId');
    const customRemoteIdAvailable = !!customRemoteId && customRemoteId !== 'null';
    let result = connection?.parameters.CallSid;

    if (customRemoteIdAvailable) result = customRemoteId;

    return result;
  }

  /**
   * Gets the correct Call Direction from the connection.
   *
   * The direction is always "Incoming" for a resumed call regardless of it's original direction.
   * Therefore if we use `connection.direction` on a outbound call that was resumed things will go wrong.
   *
   * @param   {any}     connection  The connection object given by Twilio SDK
   * @return  {string}              The correct CallDirection of the connection
   */
  static getCallDirection = (connection: any) => {
    const callWasOnHold = TwilioVoiceService.wasCallOnHold(connection);
    const connectionDirection = connection.direction === TwilioVoiceService.CallDirectionEnum.incoming ?
      CallActivityDirectionEnum.incoming : CallActivityDirectionEnum.outgoing;

    return callWasOnHold ? connection.customParameters.get('CallDirection') : connectionDirection;
  }

  /**
   * Get the incoming call type
   *  - If it's from a client, we have to handle it differently
   *  - If it's from a number, we need to handle in the regular way
   *
   * @param   {any}                   connection  Twilio connection object
   * @return  {IncomingCallTypeEnum}              Incoming call type enum
   */
  static getIncomingCallType = (connection: any) => {
    const from = connection.parameters.From;
    let result = 'Customer';

    // If the call is from a client, it's an agent call
    if (from?.startsWith('client:')) result = 'Agent';

    return result;
  }

  // TODO: Change this in the future
  // Agent calls should be possible without it being a hot transfer
  static isAgentCall = (connection: any) => connection?.customParameters?.get('isHotTransfer') === 'True'

  static isCallHotTransfer = (connection: any) => connection?.customParameters?.get('isHotTransfer') === 'True'

  static customStringParameterSafetyCheck = (value: any) => {
    let result = null;

    if (!!value && value !== 'null') result = value;

    return result;
  }

  static toTwilioSafeString = (id: any) => {
    let result = null;

    if (this.customStringParameterSafetyCheck(id)) {
      result = id
        .replaceAll(/-/g, '_hyphen_')
        .replaceAll(/\+/g, '_plus_')
        .replaceAll(/ /g, '_spc_')
        .replaceAll(/:/g, '_colon_');
    }

    return result;
  }

  static toRegularFromTwilioSafeId = (id: any) => {
    let result = null;

    if (this.customStringParameterSafetyCheck(id)) {
      result = id
        .replaceAll(/_hyphen_/g, '-')
        .replaceAll(/_plus_/g, '+')
        .replaceAll(/_spc_/g, ' ')
        .replaceAll(/_colon_/g, ':');
    }

    return result;
  }

  static toRegularBoolFromTwilioCustomParam = (bool: string) => bool === 'True'

  static toArrayFromTwilioCustomParam = (param: any) => {
    let result = null;

    // No need to proceed if the param is not given
    if (!param) return null;

    try {
      result = JSON.parse(param);
    } catch (error) {
      // Do nothing lol
    }

    return result;
  }
  /**
   * Get agent call details from connection object of a hot transfer call
   *
   * @param {any} connection Connection object from Twilio SDK
   */
  static getHotTransferCallDetailsFromConnection = (connection: any) => {
    const customParameters = connection?.customParameters;
    const customDataAvailable = !!this.customStringParameterSafetyCheck(customParameters?.get('callRecordId'));
    let result = null;

    if (customDataAvailable) {
      const agentId = this.toRegularFromTwilioSafeId(customParameters.get('agentId'));
      const agentName = this.toRegularFromTwilioSafeId(customParameters.get('agentName'));
      const agentTeamName = this.toRegularFromTwilioSafeId(customParameters.get('AgentTeamName'));
      const contactId = this.toRegularFromTwilioSafeId(customParameters.get('contactId')) || null;
      const contactRemoteId = connection.customParameters.get('ContactRemoteId') || null;
      const contactType = this.toRegularFromTwilioSafeId(customParameters.get('contactType')) || ContactTypeEnum.unknown;
      const firstName = this.toRegularFromTwilioSafeId(customParameters.get('contactFirstName')) || '';
      const lastName = this.toRegularFromTwilioSafeId(customParameters.get('contactLastName')) || '';
      const contactAccountId = this.toRegularFromTwilioSafeId(customParameters.get('contactAccountId')) || null;
      const contactAccountName = this.toRegularFromTwilioSafeId(customParameters.get('contactAccountName')) || null;
      const contactName = [firstName, lastName].filter(name => !!name && name !== 'undefined').join(' ');
      const contactNumber = this.toRegularFromTwilioSafeId(customParameters.get('contactNumber')) || null;
      const callRecordId = this.toRegularFromTwilioSafeId(customParameters.get('callRecordId'));
      const isHotTransfer = this.toRegularBoolFromTwilioCustomParam(customParameters.get('isHotTransfer'));
      const assignedTeamId = connection.customParameters.get('AssignedTeamId') || null;
      const traversedRoutes = this.toArrayFromTwilioCustomParam(connection.customParameters?.get('TraversedRoutes'));

      // Maybe we should have another param for this?
      const isConference = this.toRegularBoolFromTwilioCustomParam(customParameters.get('isHotTransfer'));

      // TODO: Remove hardcoded value after getting this correctly
      const customerOnHold = this.toRegularBoolFromTwilioCustomParam(customParameters.get('customerOnHold')) || true;

      const customerHasJoinedConference = this.toRegularBoolFromTwilioCustomParam(customParameters.get('contactHasJoined'));
      const previousCallRecordId = this.toRegularFromTwilioSafeId(customParameters.get('previousCallRecord'));
      const callDirection = TwilioVoiceService.getCallDirection(connection);
      const isIncoming = callDirection === CallActivityDirectionEnum.incoming;
      const callEventId = null;
      let callStartedAt = null;
      let callHeldAt = null;

      const firstCallStartTime = customParameters.get('firstCallStartTime');
      const callHoldTime = customParameters.get('callHoldTime');

      if (firstCallStartTime) callStartedAt = new Date(Number(firstCallStartTime) * 1000).toISOString();
      if (callHoldTime) callHeldAt = new Date(Number(callHoldTime) * 1000).toISOString();

      result = {
        // Agent information
        agentId, agentName, agentTeamName,
        // Contact information
        contactId, contactRemoteId, contactType, contactName, contactNumber, accountId: contactAccountId, accountName: contactAccountName,
        unknownCaller: contactType === ContactTypeEnum.unknown,
        // Call information
        remoteId: TwilioVoiceService.getCallRemoteId(connection), previousCallRecordId,
        customerHasJoinedConference, customerOnHold, traversedRoutes,
        isHotTransfer, isConference, callRecordId, callDirection, isIncoming, callEventId, callStartedAt, callHeldAt, assignedTeamId,
      };
    }

    return result;
  };

  /**
   * Get agent call details from connection object of a regular call
   *
   * @param {any} connection Connection object from Twilio SDK
   */
  static getContactCallDetailsFromConnection = (connection: any) => {
    const customParameters = connection?.customParameters;
    const customDataAvailable = !!this.customStringParameterSafetyCheck(customParameters?.get('CallRecordId'));
    let result = null;
    
    if (customDataAvailable) {
      const callRecordId = connection.customParameters.get('CallRecordId');
      const callDirection = TwilioVoiceService.getCallDirection(connection);
      const initCallDirection = connection.customParameters.get('CallDirection') ?? callDirection;
      const isIncomingInitiated = initCallDirection === CallActivityDirectionEnum.incoming;
      const isIncoming = callDirection === CallActivityDirectionEnum.incoming;
      const contactType = connection.customParameters.get('ContactType') || ContactTypeEnum.unknown;
      const contactId = connection.customParameters.get('ContactId') || null;
      const contactRemoteId = connection.customParameters.get('ContactRemoteId') || null;
      const firstName = connection.customParameters.get('ContactFirstName') || '';
      const lastName = connection.customParameters.get('ContactLastName') || '';
      const callEventId = connection.customParameters.get('CallEventId') || null;
      const campaignId = this.customStringParameterSafetyCheck(connection.customParameters?.get('CampaignId')) || null;
      const campaignName = this.customStringParameterSafetyCheck(connection.customParameters?.get('CampaignName')) || null;
      const accountId = this.customStringParameterSafetyCheck(connection.customParameters?.get('AccountId')) || null;
      const accountName = this.customStringParameterSafetyCheck(connection.customParameters?.get('AccountName')) || null;
      const contactName = [firstName, lastName].filter(name => !!name && name !== 'undefined').join(' ');
      const contactNumber = isIncomingInitiated ? connection.parameters.From : (connection.customParameters.get('To') ?? connection.customParameters.get('MobilePhone') ?? connection.customParameters.get('Phone'));
      const assignedTeamId = connection.customParameters.get('AssignedTeamId') || null;
      const callFromNumber = connection.parameters.From;
      const isMutedCall = connection?.mediaStream?.isMuted;
      const traversedRoutes = this.toArrayFromTwilioCustomParam(connection.customParameters?.get('TraversedRoutes'));
      const callForwardedByUserName = connection.customParameters?.get('CallForwardedByUserName');
      const callForwardedByUserId = connection.customParameters?.get('CallForwardedByUserId');
      const callNote = connection.customParameters?.get('CallNote');

      result = {
        callRecordId,
        unknownCaller: contactType === ContactTypeEnum.unknown,
        contactId,
        contactRemoteId,
        contactName,
        contactNumber,
        contactType,
        remoteId: TwilioVoiceService.getCallRemoteId(connection),
        callDirection,
        isIncoming,
        callEventId,
        campaignId,
        campaignName,
        accountId,
        accountName,
        assignedTeamId,
        callFromNumber,
        isMutedCall,
        traversedRoutes,
        callForwardedByUserId,
        callForwardedByUserName,
        callNote,
        previousCallRecordId: null,
        customerHasJoinedConference: false,
        isHotTransfer: false,
        isConference: false,
      };
    }

    return result;
  }

  /**
   * Per-Call SDK lifecycle logging for disconnect / timeout races (additive with existing `.on` handlers).
   */
  static attachCallSdkDiagnostics = (call: any, context: string) => {
    if (!call || typeof call.on !== 'function') return;
    const userId = store.getState()?.auth?.authUser?.userId;
    const snap = () => ({
      callRecordId: TwilioVoiceService.getCallDetailsFromConnection(call)?.callRecordId,
      status: typeof call.status === 'function' ? call.status() : undefined,
    });
    const logEv = (event: string, extra?: Record<string, unknown>) =>
      logger.info(`${LogTitle} Call SDK [${context}] ${event}`, LogTitle, { userId, ...snap(), ...extra });

    call.on('accept', () => logEv('accept'));
    call.on('disconnect', () => logEv('disconnect'));
    call.on('cancel', () => logEv('cancel'));
    call.on('reject', () => logEv('reject'));
    call.on('error', (e: any) => logEv('error', { error: e }));
    call.on('reconnecting', (e: any) => logEv('reconnecting', { error: e }));
    call.on('reconnected', () => {
      logEv('reconnected');
      const callRecordId = TwilioVoiceService.getCallDetailsFromConnection(call)?.callRecordId;
      TwilioVoiceService.streamProcessManager.reattachMediaAfterTwilioReconnect(callRecordId, 'twilio_reconnected');
    });
    call.on('transportClose', () => logEv('transportClose'));
  };

  /**
   * Gets the call details from the connection object.
   *
   * @param   {any}  connection  The connection object given by Twilio SDK
   * @return  {any}              The call details object
   */
  static getCallDetailsFromConnection = (connection: any) => {
    // const isAgentCall = this.isAgentCall(connection);
    const isHotTransferCall = this.isCallHotTransfer(connection);
    let result = null;
    if (isHotTransferCall) {
      result = this.getHotTransferCallDetailsFromConnection(connection);
    } else {
      result = this.getContactCallDetailsFromConnection(connection);
    }

    return result;
  }
}
 